import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Store,
  Plus,
  RefreshCw,
  Trash2,
  MapPin,
  Star,
  ShoppingBag,
  ExternalLink,
} from "lucide-react";
import { apiClient } from "../lib/api";
import type { Shop } from "@erc/shared";

function TrackShopForm({ onSuccess }: { onSuccess: () => void }) {
  const [url, setUrl] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: (url: string) => apiClient.trackShop({ url }),
    onSuccess: () => {
      setUrl("");
      setIsOpen(false);
      onSuccess();
    },
  });

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Mağaza Takip Et
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (url.trim()) mutation.mutate(url.trim());
      }}
      className="flex items-center gap-2"
    >
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://www.etsy.com/shop/MagazaAdi"
        className="w-96 rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        autoFocus
      />
      <button
        type="submit"
        disabled={mutation.isPending}
        className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors disabled:opacity-50"
      >
        {mutation.isPending ? "Taranıyor..." : "Takip Et"}
      </button>
      <button
        type="button"
        onClick={() => setIsOpen(false)}
        className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-50"
      >
        İptal
      </button>
      {mutation.isError && (
        <p className="text-sm text-red-500">
          {(mutation.error as Error).message}
        </p>
      )}
    </form>
  );
}

function ShopCard({ shop }: { shop: Shop }) {
  const queryClient = useQueryClient();
  const latestSnapshot = shop.snapshots?.[0];

  const refreshMutation = useMutation({
    mutationFn: () => apiClient.refreshShop(shop.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["shops"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.deleteShop(shop.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["shops"] }),
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <Link to={`/shops/${shop.id}`} className="flex items-start gap-3 min-w-0 flex-1">
          {shop.avatarUrl ? (
            <img
              src={shop.avatarUrl}
              alt={shop.name}
              className="h-12 w-12 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-100">
              <Store className="h-6 w-6 text-orange-600" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{shop.name}</h3>
            {shop.location && (
              <p className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                <MapPin className="h-3 w-3" />
                {shop.location}
              </p>
            )}
          </div>
        </Link>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          <a
            href={shop.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Etsy'de aç"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50"
            title="Yenile"
          >
            <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => {
              if (confirm("Bu mağazayı takipten çıkarmak istediğinize emin misiniz?")) {
                deleteMutation.mutate();
              }
            }}
            className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
            title="Takibi kaldır"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-gray-100 pt-3">
        <div>
          <p className="text-xs text-gray-500">Satış</p>
          <p className="text-sm font-semibold text-gray-900">
            {latestSnapshot?.totalSales?.toLocaleString() ?? "—"}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Yorum</p>
          <p className="text-sm font-semibold text-gray-900">
            {latestSnapshot?.totalReviews?.toLocaleString() ?? "—"}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Puan</p>
          <p className="flex items-center gap-1 text-sm font-semibold text-gray-900">
            {latestSnapshot?.rating ? (
              <>
                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                {latestSnapshot.rating.toFixed(1)}
              </>
            ) : (
              "—"
            )}
          </p>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
        <ShoppingBag className="h-3 w-3" />
        {shop._count?.listings ?? 0} ürün
      </div>
    </div>
  );
}

export default function ShopsPage() {
  const queryClient = useQueryClient();

  const { data: shops, isLoading } = useQuery({
    queryKey: ["shops"],
    queryFn: () => apiClient.listShops(),
  });

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mağaza Takip</h2>
          <p className="mt-1 text-sm text-gray-500">
            Etsy mağazalarını takip et, ürünlerini ve istatistiklerini analiz et.
          </p>
        </div>
        <TrackShopForm onSuccess={() => queryClient.invalidateQueries({ queryKey: ["shops"] })} />
      </div>

      {/* Shops grid */}
      {isLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-44 animate-pulse rounded-xl border border-gray-200 bg-gray-100"
            />
          ))}
        </div>
      ) : !shops || shops.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 py-20 text-center">
          <Store className="mb-3 h-12 w-12 text-gray-300" />
          <p className="text-base font-medium text-gray-500">
            Henüz takip edilen mağaza yok
          </p>
          <p className="mt-1 text-sm text-gray-400">
            Bir Etsy mağaza URL'si girerek takip etmeye başla
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {shops.map((shop: Shop) => (
            <ShopCard key={shop.id} shop={shop} />
          ))}
        </div>
      )}
    </div>
  );
}
